import React from 'react';
import * as Sentry from '@sentry/react';
import {Navigate, Route, Routes as BaseRoutes} from 'react-router-dom';
import app from '@src/helpers/app';
import {IClientRoute} from '@src/@interfaces/services/Router';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import Error404 from '@views/errors/Error404';
import ViewRepresentation from '@src/components/ViewRepresentation/ViewRepresentation';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(BaseRoutes);

const NotFoundView = () => <ViewRepresentation element={Error404}/>;

export default class Routes extends React.Component {
    protected protectedRoute = ProtectedRoute;
    protected publicRoute = PublicRoute;

    renderFallback() {
        return <Route path="*" element={<NotFoundView/>}/>;
    }

    render() {
        const routes = app('client-router').getRoutes();

        return <SentryRoutes>
            {this.renderRoutes(routes)}

            {this.renderFallback()}
        </SentryRoutes>;
    }

    protected renderRoutes(routes: IClientRoute[]): (JSX.Element | null)[] {
        return routes.map(config => {
            let element;
            if (config.isRedirect()) {
                element = <Navigate to={config.getRedirectPath() as string} replace={true}/>;
            } else {
                element = this.getRouteElement(config) ?? <NotFoundView/>;
            }

            return <Route
                    key={`${config.getName()}-${config.getPath()}`}
                    path={config.getPath()}
                    element={element}
            />;
        });
    }

    protected getRouteElement(route: IClientRoute): JSX.Element | null {
        const stringAction = route.getAction();
        if (!stringAction) {
            return null;
        }

        const repo = app('controllers');
        const action = repo.parseRouteAction(stringAction);
        const controller = repo.getControllerByName(action.controllerName);
        if (!controller) {
            return null;
        }

        const Element = route.isProtected() ? this.protectedRoute : this.publicRoute;

        return <Element
                controller={controller}
                action={action.controllerAction}
                permissions={route.getPermissions()}
        >
            {this.renderRoutes(controller.getActionsRoutes())}
        </Element>;
    }
}
